import React, { useState, useEffect } from 'react';
import { Mail, Lock, User as UserIcon, ArrowLeft, Shield, Smartphone } from 'lucide-react';
import Hyperspeed from '../components/Hyperspeed';
import AnimatedLogo from '../components/AnimatedLogo';
import awsLogo from '../assets/logos/aws-logo.svg';
import gcpLogo from '../images/gcp-logo.png';
import azureLogo from '../assets/logos/azure-logo.svg';
import { API_BASE_URL } from '../config';
import './AuthPage.css';

const API_BASE = API_BASE_URL;

// ─── SVG Icons ────────────────────────────────────────────────────────────────

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" fill="#EA4335" />
  </svg>
);

const GitHubIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
  </svg>
);

// ─── Helper: check local trusted device ───────────────────────────────────────

async function checkTrustedDevice(email) {
  const storedToken = localStorage.getItem('dms_trusted_device_token');
  const storedEmail = localStorage.getItem('dms_trusted_device_email');
  if (!storedToken || storedEmail !== email) return null;

  try {
    const res = await fetch(`${API_BASE}/api/auth/verify-trusted-device`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, deviceToken: storedToken })
    });
    const data = await res.json();
    if (res.ok && data.success && data.trusted) return data;
    localStorage.removeItem('dms_trusted_device_token');
    localStorage.removeItem('dms_trusted_device_email');
    return null;
  } catch {
    return null;
  }
}

// ─── Main Component ───────────────────────────────────────────────────────────

function AuthPage({ onBack, onLoginSuccess, initialMode = 'login', theme = 'dark' }) {
  const [animationClass, setAnimationClass] = useState(initialMode === 'register' ? 'active' : '');

  // steps: 'email' | 'methods' | 'enter_password' | 'otp_sent' | 'no_account'
  const [loginStep, setLoginStep]       = useState('email');
  const [detectedEmail, setDetectedEmail] = useState('');
  const [authMethods, setAuthMethods]   = useState([]);
  const [emailChecking, setEmailChecking] = useState(false);

  // Password step
  const [loginPassword, setLoginPassword] = useState('');
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);

  // Register
  const [registerName, setRegisterName]       = useState('');
  const [registerEmail, setRegisterEmail]     = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptTerms, setAcceptTerms]         = useState(false);

  // Messages
  const [errorMsg, setErrorMsg]   = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // OTP
  const [rememberDevice, setRememberDevice] = useState(false);
  const [otpSending, setOtpSending]         = useState(false);

  // Resend verification
  const [showResend, setShowResend]       = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');

  const emailRegex    = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>_\-+=~`[\]\\;'/]).{8,}$/;

  useEffect(() => {
    if (initialMode === 'register') {
      setAnimationClass('active');
      setErrorMsg('');
      setSuccessMsg('');
    } else {
      setAnimationClass('');
      setLoginStep('email');
      setDetectedEmail('');
      setAuthMethods([]);
      setLoginPassword('');
      setForgotPasswordMode(false);
      setErrorMsg('');
      setSuccessMsg('');
    }
  }, [initialMode]);

  const switchToRegister = () => {
    setAnimationClass('active');
    setErrorMsg(''); setSuccessMsg('');
    setForgotPasswordMode(false); setShowResend(false);
    window.location.hash = '#/register';
  };
  const switchToLogin = () => {
    setAnimationClass('');
    setErrorMsg(''); setSuccessMsg('');
    setLoginStep('email'); setDetectedEmail(''); setAuthMethods([]);
    setLoginPassword(''); setForgotPasswordMode(false);
    window.location.hash = '#/login';
  };

  const clearMessages = () => { setErrorMsg(''); setSuccessMsg(''); };

  const completeLoginSuccess = (token, message = 'Login successful! Redirecting…') => {
    setSuccessMsg(message);
    localStorage.setItem('dms_auth_token', token);
    window.dispatchEvent(new Event('dms-auth-updated'));
    setTimeout(() => { onLoginSuccess(); }, 1200);
  };

  // ── Step 1: Check email ─────────────────────────────────────────────────────

  const handleEmailContinue = async (e) => {
    e.preventDefault();
    if (!detectedEmail || !emailRegex.test(detectedEmail)) {
      setErrorMsg('Please enter a valid email address.');
      return;
    }
    clearMessages();
    setEmailChecking(true);

    try {
      const trusted = await checkTrustedDevice(detectedEmail);
      if (trusted) {
        completeLoginSuccess(trusted.token, 'Recognized device — logging you in…');
        return;
      }

      const res = await fetch(`${API_BASE}/api/auth/check-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: detectedEmail })
      });

      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        setErrorMsg(`Server error (${res.status}). Is the backend running? Try restarting it.`);
        return;
      }

      const data = await res.json();

      if (!res.ok) {
        setErrorMsg(data.message || `Server responded with ${res.status}.`);
        return;
      }

      if (!data.account_exists) {
        setLoginStep('no_account');
        return;
      }

      setAuthMethods(data.auth_methods || ['otp']);
      setLoginStep('methods');
    } catch (err) {
      setErrorMsg('Cannot reach the server. Please check your connection and try again.');
    } finally {
      setEmailChecking(false);
    }
  };

  // ── Step 2→3: Password ──────────────────────────────────────────────────────

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!loginPassword) { setErrorMsg('Please enter your password.'); return; }
    clearMessages();

    try {
      const res  = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: detectedEmail, password: loginPassword })
      });
      const data = await res.json();

      if (!res.ok || !data.success) {
        if (data.message && data.message.includes('verify your email')) {
          setUnverifiedEmail(detectedEmail); setShowResend(true);
        }
        throw new Error(data.message || 'Login failed');
      }

      completeLoginSuccess(data.token);
    } catch (err) {
      setErrorMsg(err.message || 'An error occurred.');
    }
  };

  // ── Step 2→OTP: Send OTP ────────────────────────────────────────────────────

  const handleSendOtp = async () => {
    clearMessages();
    setOtpSending(true);
    try {
      const res  = await fetch(`${API_BASE}/api/auth/send-login-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: detectedEmail })
      });
      const data = await res.json();

      if (res.status === 429) {
        setErrorMsg(data.message || 'Too many requests. Please wait 10 minutes.');
        return;
      }
      if (!data.success) throw new Error(data.message || 'Failed to send code');

      const intent = `login${rememberDevice ? '&remember=1' : ''}`;
      setTimeout(() => {
        window.location.href = `#/verify-email?email=${encodeURIComponent(detectedEmail)}&intent=${intent}`;
      }, 600);
      setLoginStep('otp_sent');
    } catch (err) {
      setErrorMsg(err.message || 'Failed to send code.');
    } finally {
      setOtpSending(false);
    }
  };

  // ── SSO buttons ─────────────────────────────────────────────────────────────

  const handleGoogleSSO = () => { window.location.href = `${API_BASE}/api/auth/google`; };
  const handleGitHubSSO = () => { window.location.href = `${API_BASE}/api/auth/github`; };

  // ── Forgot password ─────────────────────────────────────────────────────────

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    if (!detectedEmail || !emailRegex.test(detectedEmail)) {
      setErrorMsg('Please enter a valid email address.'); return;
    }
    clearMessages();
    try {
      const res  = await fetch(`${API_BASE}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: detectedEmail })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Request failed');
      setSuccessMsg(data.message);
      setTimeout(() => {
        window.location.href = `#/reset-password?email=${encodeURIComponent(detectedEmail)}`;
      }, 1000);
    } catch (err) {
      setErrorMsg(err.message || 'An error occurred.');
    }
  };

  // ── Resend verification ─────────────────────────────────────────────────────

  const handleResendVerification = async () => {
    clearMessages();
    try {
      const res  = await fetch(`${API_BASE}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: unverifiedEmail })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSuccessMsg(data.message);
        setTimeout(() => {
          window.location.href = `#/verify-email?email=${encodeURIComponent(unverifiedEmail)}&intent=verify`;
        }, 1000);
      } else {
        throw new Error(data.message || 'Request failed');
      }
    } catch (err) {
      setErrorMsg(err.message || 'An error occurred.');
    }
  };

  // ── Register ────────────────────────────────────────────────────────────────

  const isRegisterValid = () =>
    registerName &&
    registerEmail &&
    registerPassword &&
    confirmPassword &&
    acceptTerms;

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    if (!registerName || !registerEmail || !registerPassword || !confirmPassword) {
      setErrorMsg('Please fill in all fields.'); return;
    }
    if (!emailRegex.test(registerEmail)) { setErrorMsg('Please enter a valid email address.'); return; }
    if (!passwordRegex.test(registerPassword)) {
      setErrorMsg('Password must contain at least 8 characters, one uppercase letter, one lowercase letter, one number, and one special character.'); return;
    }
    if (registerPassword !== confirmPassword) { setErrorMsg('Passwords do not match.'); return; }
    if (!acceptTerms) { setErrorMsg('You must accept the terms & conditions.'); return; }
    clearMessages();

    try {
      const res  = await fetch(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: registerName, email: registerEmail, password: registerPassword })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Registration failed');
      setSuccessMsg(data.message);
      setRegisterName(''); setRegisterEmail(''); setRegisterPassword(''); setConfirmPassword('');
      setTimeout(() => {
        window.location.href = `#/verify-email?email=${encodeURIComponent(registerEmail)}&intent=verify`;
      }, 1000);
    } catch (err) {
      setErrorMsg(err.message || 'An error occurred during registration.');
    }
  };

  const isLoginPaneActive = animationClass !== 'active';

  const renderErrorBadge = () => {
    if (!errorMsg || !isLoginPaneActive) return null;
    return (
      <div className="auth-error-badge">
        {errorMsg}
        {showResend && (
          <div style={{ marginTop: '10px' }}>
            <button className="auth-submit-btn" style={{ padding: '5px 10px', fontSize: '12px' }} onClick={handleResendVerification}>
              Resend Verification Email
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderSuccessBadge = () => {
    if (!successMsg || !isLoginPaneActive) return null;
    return <div className="auth-success-badge">{successMsg}</div>;
  };

  const renderEmailStep = () => (
    <div className="ef-email-step">
      <form onSubmit={handleEmailContinue} className="ef-form">
        <div className="input-group">
          <Mail size={16} className="input-icon" />
          <input
            id="login-email-input"
            type="email"
            placeholder="Email Address"
            value={detectedEmail}
            onChange={(e) => { setDetectedEmail(e.target.value); clearMessages(); }}
            autoFocus
            required
          />
        </div>
        <button
          type="submit"
          className="auth-submit-btn"
          disabled={emailChecking || !detectedEmail || !emailRegex.test(detectedEmail)}
        >
          {emailChecking ? 'Checking…' : 'Continue →'}
        </button>
      </form>

      <div className="divider-text" style={{ margin: '1.5rem 0 1.25rem' }}>──────── OR SIGN IN USING ────────</div>

      <div className="sso-buttons-container">
        <button type="button" className="google-sso-btn" onClick={handleGoogleSSO}>
          <GoogleIcon />
          <span style={{ marginLeft: 10 }}>Continue with Google</span>
        </button>
      </div>
    </div>
  );

  const renderMethodsStep = () => {
    const hasPassword = authMethods.includes('password');
    const hasGoogle   = authMethods.includes('google');
    const hasGitHub   = authMethods.includes('github');

    return (
      <div className="ef-methods">
        <p className="ef-email-badge">
          <Mail size={13} style={{ marginRight: 6, opacity: 0.7 }} />
          {detectedEmail}
        </p>

        <p className="ef-methods-label">Choose how to sign in:</p>

        <div className="ef-method-list">
          {hasPassword && (
            <button
              id="btn-method-password"
              className="ef-method-btn ef-method-password"
              onClick={() => { clearMessages(); setLoginStep('enter_password'); }}
            >
              <Lock size={18} className="ef-method-icon" />
              <span>Continue with Password</span>
              <span className="ef-method-arrow">›</span>
            </button>
          )}

          {hasGoogle && (
            <button
              id="btn-method-google"
              className="ef-method-btn ef-method-google"
              onClick={handleGoogleSSO}
            >
              <GoogleIcon />
              <span>Continue with Google</span>
              <span className="ef-method-arrow">›</span>
            </button>
          )}

          {hasGitHub && (
            <button
              id="btn-method-github"
              className="ef-method-btn ef-method-github"
              onClick={handleGitHubSSO}
            >
              <GitHubIcon />
              <span>Continue with GitHub</span>
              <span className="ef-method-arrow">›</span>
            </button>
          )}

          <button
            id="btn-method-otp"
            className="ef-method-btn ef-method-otp"
            onClick={handleSendOtp}
            disabled={otpSending}
          >
            <Shield size={18} className="ef-method-icon" />
            <span>{otpSending ? 'Sending code…' : 'Login via OTP (Email Code)'}</span>
            <span className="ef-method-arrow">›</span>
          </button>
        </div>

        <label className="ef-remember-label" htmlFor="remember-device-chk">
          <input
            type="checkbox"
            id="remember-device-chk"
            checked={rememberDevice}
            onChange={(e) => setRememberDevice(e.target.checked)}
          />
          <span className="checkbox-custom" />
          <Smartphone size={13} style={{ marginRight: 5 }} />
          <span className="label-text">Remember this device for 30 days (OTP)</span>
        </label>

        <button
          className="ef-back-link"
          onClick={() => { clearMessages(); setLoginStep('email'); setAuthMethods([]); }}
        >
          <ArrowLeft size={14} /> Use a different email
        </button>
      </div>
    );
  };

  const renderPasswordStep = () => (
    <div className="ef-form">
      <p className="ef-email-badge">
        <Mail size={13} style={{ marginRight: 6, opacity: 0.7 }} />
        {detectedEmail}
      </p>

      {forgotPasswordMode ? (
        <form onSubmit={handleForgotPasswordSubmit}>
          <p className="ef-methods-label" style={{ marginBottom: '1rem' }}>
            We'll send a reset code to <strong>{detectedEmail}</strong>.
          </p>
          <button type="submit" className="auth-submit-btn">
            Send Reset Code
          </button>
          <button
            type="button"
            className="ef-back-link"
            style={{ marginTop: '10px' }}
            onClick={() => { setForgotPasswordMode(false); clearMessages(); }}
          >
            <ArrowLeft size={14} /> Back
          </button>
        </form>
      ) : (
        <form onSubmit={handlePasswordLogin}>
          <div className="input-group" style={{ marginBottom: '0.65rem' }}>
            <Lock size={16} className="input-icon" />
            <input
              id="login-password-input"
              type="password"
              placeholder="Password"
              value={loginPassword}
              onChange={(e) => { setLoginPassword(e.target.value); clearMessages(); }}
              autoFocus
              required
            />
          </div>

          <div className="auth-form-footer" style={{ marginBottom: '0.7rem' }}>
            <span />
            <span className="forgot-password">
              <a href="#forgot" onClick={(e) => { e.preventDefault(); setForgotPasswordMode(true); clearMessages(); }}>
                Forgot password?
              </a>
            </span>
          </div>

          <button type="submit" className="auth-submit-btn" disabled={!loginPassword}>
            Sign In
          </button>
        </form>
      )}

      <button
        className="ef-back-link"
        onClick={() => { clearMessages(); setLoginStep('methods'); setLoginPassword(''); setForgotPasswordMode(false); }}
      >
        <ArrowLeft size={14} /> Other login options
      </button>
    </div>
  );

  const renderNoAccountStep = () => (
    <div className="ef-no-account">
      <div className="ef-no-account-icon">?</div>
      <p className="ef-no-account-title">No account found</p>
      <p className="ef-no-account-sub">
        No account is associated with <strong>{detectedEmail}</strong>.
      </p>
      <button className="auth-submit-btn" style={{ marginTop: '1rem' }} onClick={switchToRegister}>
        Create Workspace
      </button>
      <button
        className="ef-back-link"
        onClick={() => { clearMessages(); setLoginStep('email'); setDetectedEmail(''); }}
      >
        <ArrowLeft size={14} /> Try a different email
      </button>
    </div>
  );

  const renderOtpSentStep = () => (
    <div className="ef-otp-sent">
      <div className="ef-otp-sent-icon">✉</div>
      <p className="ef-otp-sent-title">Check your inbox</p>
      <p className="ef-otp-sent-sub">
        A 6-digit login code was sent to <strong>{detectedEmail}</strong>.
        It expires in 10 minutes.
      </p>
    </div>
  );

  const renderLoginContent = () => {
    switch (loginStep) {
      case 'methods':       return renderMethodsStep();
      case 'enter_password': return renderPasswordStep();
      case 'no_account':    return renderNoAccountStep();
      case 'otp_sent':      return renderOtpSentStep();
      default:              return renderEmailStep();
    }
  };

  const stepTitles = {
    email:          'Welcome Back',
    methods:        'Sign In',
    enter_password: 'Enter Password',
    no_account:     'Account Not Found',
    otp_sent:       'Code Sent'
  };

  return (
    <div className="auth-page-wrapper">
      <div className="hyperspeed-bg">
        <Hyperspeed />
      </div>

      <button className="auth-back-btn" onClick={onBack} aria-label="Back to Home">
        <ArrowLeft size={20} />
      </button>

      <div className="auth-page-card-container">
        <div className={`auth-container ${animationClass}`}>
          <div className="auth-left-panel">
            <div className="floating-logo float-aws"><img src={awsLogo} alt="AWS" /></div>
            <div className="floating-logo float-gcp"><img src={gcpLogo} alt="GCP" /></div>
            <div className="floating-logo float-azure"><img src={azureLogo} alt="Azure" /></div>
            <div className="auth-left-content">
              <div className="auth-avatar-logo-wrapper" style={{ marginBottom: '1.8rem' }}>
                <AnimatedLogo />
              </div>
              <div className="auth-brand-heading-wrapper">
                <h2 className="auth-brand-heading">
                  {"Datalift".split("").map((char, index) => (
                    <span
                      key={index}
                      className="bounce-letter"
                      style={{ animationDelay: `${index * 0.08}s` }}
                    >
                      {char}
                    </span>
                  ))}
                </h2>
              </div>
              <p>Move data faster, migrate smarter. Easily execute database migrations across clouds with real-time replication and transaction monitoring.</p>
              <div className="auth-feature-chips">
                <span className="chip active">PostgreSQL</span>
                <span className="chip disabled">MySQL (Coming Soon)</span>
                <span className="chip disabled">SQL Server (Coming Soon)</span>
                <span className="chip disabled">MongoDB (Coming Soon)</span>
              </div>
            </div>
          </div>

          <div className="auth-right-panel">
            <div className="auth-tabs">
              <button
                type="button"
                className={`auth-tab ${isLoginPaneActive ? 'active' : ''}`}
                onClick={switchToLogin}
              >
                Sign In
              </button>
              <button
                type="button"
                className={`auth-tab ${!isLoginPaneActive ? 'active' : ''}`}
                onClick={switchToRegister}
              >
                Create Workspace
              </button>
            </div>

            {isLoginPaneActive ? (
              <div className="auth-content login-content fade-in">
                <h1>{stepTitles[loginStep] || 'Welcome Back'}</h1>
                <p className="auth-form-subtitle">
                  Access migration jobs, CDC streams, replication monitoring and dashboards.
                </p>

                {renderErrorBadge()}
                {renderSuccessBadge()}

                {renderLoginContent()}

                <div className="auth-switch-link">
                  <span className="text-muted">Don't have a workspace?</span>{' '}
                  <button type="button" className="switch-btn" onClick={switchToRegister}>
                    Create Workspace
                  </button>
                </div>
              </div>
            ) : (
              <div className="auth-content register-content fade-in">
                <h1>Create Migration Workspace</h1>
                <p className="auth-form-subtitle">
                  Create a secure workspace for managing database migrations and replication pipelines.
                </p>

                {animationClass === 'active' && errorMsg  && <div className="auth-error-badge">{errorMsg}</div>}
                {animationClass === 'active' && successMsg && <div className="auth-success-badge">{successMsg}</div>}

                <form onSubmit={handleRegisterSubmit}>
                  <div className="input-group">
                    <UserIcon size={16} className="input-icon" />
                    <input
                      type="text" placeholder="Workspace Name"
                      value={registerName} onChange={(e) => setRegisterName(e.target.value)} required
                    />
                  </div>
                  <div className="input-group">
                    <Mail size={16} className="input-icon" />
                    <input
                      type="email" placeholder="Admin Email Address"
                      value={registerEmail} onChange={(e) => setRegisterEmail(e.target.value)} required
                    />
                  </div>
                  <div className="input-group">
                    <Lock size={16} className="input-icon" />
                    <input
                      type="password" placeholder="Password"
                      value={registerPassword} onChange={(e) => setRegisterPassword(e.target.value)} required
                    />
                  </div>
                  <div className="input-group">
                    <Lock size={16} className="input-icon" />
                    <input
                      type="password" placeholder="Confirm Password"
                      value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required
                    />
                  </div>

                  <div className="auth-form-footer">
                    <label className="remember-me" htmlFor="terms-checkbox">
                      <input
                        type="checkbox" id="terms-checkbox"
                        checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)}
                      />
                      <span className="checkbox-custom" />
                      <span className="label-text">I accept the terms &amp; conditions</span>
                    </label>
                  </div>

                  <button type="submit" className="auth-submit-btn" disabled={!isRegisterValid()}>
                    Create Workspace
                  </button>
                </form>

                <div className="divider-text" style={{ margin: '1.5rem 0 1.25rem' }}>──────── OR CREATE USING ────────</div>

                <div className="sso-buttons-container">
                  <button type="button" className="google-sso-btn" onClick={handleGoogleSSO}>
                    <GoogleIcon />
                    <span style={{ marginLeft: 10 }}>Continue with Google</span>
                  </button>
                </div>

                <div className="auth-switch-link">
                  <span className="text-muted">Already managing migrations?</span>{' '}
                  <button type="button" className="switch-btn" onClick={switchToLogin}>
                    Sign In
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AuthPage;
